import React, { Component } from "react";
import { connect } from "react-redux";
import { updatePredictions } from "./store/actions";
import { injectIntl } from "react-intl";
import { PlainText } from "@learningblocks/simulations/shared-components";
import style from "./style.module.scss";

/**
 * RestrictionDigestPredictionsBase Component.
 * Handles user input for predictions and displays the results.
 */
class RestrictionDigestPredictionsBase extends Component {
  /**
   * Update the answer based on user input and its type (numeric/alphabet).
   * Validates the input before updating the state.
   */
  updateAnswer = (question, answer, e, type = "numeric") => {
    const newValue = e.target.value;

    let isValid = false;
    let errorMessage =
      type === "numeric"
        ? this.props.intl.formatMessage({
            id: this.props.error_numeric,
            defaultMessage: this.props.error_numeric,
          })
        : this.props.intl.formatMessage({
            id: this.props.error_text,
            defaultMessage: this.props.error_text,
          });

    if (newValue === "") isValid = true;
    else if (type === "numeric" && /^[0-9]+$/.test(newValue)) isValid = true;
    else if (type === "alphabet" && /^[a-zA-Z]+$/.test(newValue))
      isValid = true;

    if (isValid) {
      this.props.updatePredictions({ question, answer, value: newValue });
      e.target.setCustomValidity("");
    } else {
      e.target.setCustomValidity(errorMessage);
      e.target.reportValidity();
    }
  };

  /**
   * Helper function to create input elements.
   * Reduces duplication of creating multiple similar input fields.
   */
  createInputField = (question, answer, type = "numeric") => (
    <input
      className={style.QuestionInput}
      type="text"
      placeholder={this.props.intl.formatMessage({
        id: this.props.placeholder,
        defaultMessage: this.props.placeholder,
      })}
      onChange={(e) => this.updateAnswer(question, answer, e, type)}
      value={this.props.predictions[question][answer]}
      dir={this.props.intl.locale == "ar" ? "rtl" : "ltr"}
    />
  );

  buildQ1 = () => (
    <div key="Q1" className={style.QuestionSection}>
      <PlainText text={this.props.question_1} />
      <div className={style.QuestionLine}>
        <label className={style.QuestionLabel}>a. pKAN-R:</label>
        {this.createInputField("1", "1")}
        <label className={style.QuestionLabel}>b. pARA:</label>
        {this.createInputField("1", "2")}
      </div>
    </div>
  );

  buildQ2 = () => (
    <div key="Q2" className={style.QuestionSection}>
      <PlainText text={this.props.question_2} />
      <div className={style.QuestionLine}>
        <label className={style.QuestionLabel}>a. pKAN-R:</label>
        {this.createInputField("2", "1")}
        <label className={style.QuestionLabel}>and</label>
        {this.createInputField("2", "2")}
      </div>
      <div className={style.QuestionLine}>
        <label className={style.QuestionLabel}>b. pARA:</label>
        {this.createInputField("3", "1")}
        <label className={style.QuestionLabel}>and</label>
        {this.createInputField("3", "2")}
      </div>
    </div>
  );

  buildQ3 = () => (
    <div key="Q3" className={style.QuestionSection}>
      <PlainText text={this.props.question_3} />
      <div>
        <div className={style.ChoiceList}>
          {["kanR", "rfp", "pBAD", "ampR", "araC", "ori", "nogene"].map(
            (gene, index) => (
              <div
                key={index}
                className={`${style.ChoiceListItem} ${style[gene]}`}
              >
                <i>{gene}</i>
              </div>
            )
          )}
        </div>
        <div>
          <div className={style.QuestionLine}>
            <label className={style.QuestionLabel}>a. pKAN-R:</label>
            {this.createInputField("4", "1", "alphabet")}
            <label className={style.QuestionLabel}>and</label>
            {this.createInputField("4", "2", "alphabet")}
          </div>
          <div className={style.QuestionLine}>
            <label className={style.QuestionLabel}>b. pARA:</label>
            {this.createInputField("5", "1", "alphabet")}
            <label className={style.QuestionLabel}>and</label>
            {this.createInputField("5", "2", "alphabet")}
          </div>
        </div>
      </div>
    </div>
  );

  buildQuestionBlock = () => {
    return (
      <div className={style.QuestionsFlex}>
        {this.buildQ1()}
        {this.props.predictions["1"]["1"] && this.props.predictions["1"]["2"]
          ? [this.buildQ2(), this.buildQ3()]
          : null}
      </div>
    );
  };

  renderResults = () => {
    const renderSection = (label, value) => (
      <div className={style.Section}>
        <label className={style.Label}>{label}</label>
        <span className={style.Value}>{value ? value : 0} bp</span>
      </div>
    );

    return (
      <div>
        <div className={style.Results}>
          {renderSection("a. pKAN-R:", this.props.predictions["2"]["1"])}
          {renderSection(" and ", this.props.predictions["2"]["2"])}
        </div>
        <div className={style.Results}>
          {renderSection("b. pARA:", this.props.predictions["3"]["1"])}
          {renderSection("and", this.props.predictions["3"]["2"])}
        </div>
      </div>
    );
  };

  render() {
    return this.props.component_type === "predicts"
      ? this.buildQuestionBlock()
      : this.renderResults();
  }
}

const mapStateToProps = (state) => ({
  predictions: state.predictions,
});

export const RestrictionDigestPredictions = connect(mapStateToProps, {
  updatePredictions,
})(injectIntl(RestrictionDigestPredictionsBase));
